import React, { useEffect, useState } from 'react';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Scales, Warning } from '@phosphor-icons/react';

const Bar = ({ value, max }) => (
  <div className="score-bar" style={{ width: '100%' }}>
    <div className="score-bar-fill" style={{ width: `${(value / max) * 100}%` }} />
  </div>
);

const Group = ({ title, rows, disparity }) => (
  <div className="border border-border bg-white p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="label-eyebrow text-muted-foreground">{title}</div>
      {disparity !== null && disparity !== undefined && (
        <div className={`label-eyebrow px-2 py-0.5 ${disparity > 20 ? 'bg-destructive text-destructive-foreground' : disparity > 10 ? 'bg-accent text-accent-foreground' : 'bg-primary text-primary-foreground'}`}>
          DISPARITY · {disparity}
        </div>
      )}
    </div>
    {rows.length === 0 ? (
      <div className="text-sm text-muted-foreground">Not enough data yet.</div>
    ) : (
      <ul className="space-y-4">
        {rows.map((r) => (
          <li key={r.group}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium">{r.group}</span>
              <span className="mono text-xs text-muted-foreground">n={r.n} · mean {r.mean}</span>
            </div>
            <Bar value={r.mean} max={100} />
            <div className="mono text-xs text-muted-foreground mt-1">median {r.median} · range {r.min}-{r.max}</div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const Fairness = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/fairness/overview').then((r) => setData(r.data));
  }, []);

  if (!data) return <Shell><div className="p-8 mono text-sm">Loading...</div></Shell>;

  return (
    <Shell>
      <div className="max-w-[1300px] mx-auto px-6 py-10">
        <div className="label-eyebrow text-primary mb-2">ADMIN · FAIRNESS AUDIT</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-3">Who is Hikari scoring, and how?</h1>
        <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed mb-8">
          Aggregate-only view of score distributions by demographic slices. Used to detect and mitigate
          bias in the scoring engine. No individual candidate data is shown here.
        </p>

        {data.total_scored_candidates < 5 && (
          <div className="border border-accent bg-accent/10 p-4 mb-8 flex items-center gap-3 text-sm" data-testid="fairness-thin-data">
            <Warning size={20} weight="duotone" className="text-accent-foreground" />
            Only {data.total_scored_candidates} scored candidates — disparities are statistically unreliable below n=5 per group.
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <Group title="BY COUNTRY" rows={data.by_country} disparity={data.disparity_country} />
          <Group title="BY WORK AUTHORIZATION" rows={data.by_work_authorization} disparity={data.disparity_work_authorization} />
        </div>

        <div className="mt-8 border border-border p-6 bg-secondary">
          <div className="label-eyebrow text-muted-foreground mb-2">HOW TO READ THIS</div>
          <ul className="text-sm space-y-1 text-muted-foreground leading-relaxed">
            <li>· <b>Disparity</b> = largest mean gap between groups with n ≥ 3.</li>
            <li>· <span className="mono">&lt; 10</span>: healthy. <span className="mono">10-20</span>: review assessment content. <span className="mono">&gt; 20</span>: investigate scoring weights and rubrics.</li>
            <li>· If one group has very few scored candidates, the signal is likely noise — increase sample before acting.</li>
          </ul>
        </div>
      </div>
    </Shell>
  );
};

export default Fairness;
